import { authHeader, clearSession } from './auth';

export interface PersonalSkillSummary {
  id: string;
  name: string;
  description: string | null;
  scope: 'personal' | 'team' | 'community' | 'bundled';
  lastModifiedAt: string;
  updatedAt: string;
}

export interface PersonalSkillDetail extends PersonalSkillSummary {
  body: string;
  frontmatter: Record<string, unknown>;
  contentHash: string;
  versionN: number;
  lastPublishedToUrl?: string | null;
  lastPublishedToSha?: string | null;
  lastPublishedAt?: string | null;
  importedFrom?: string | null;
}

export interface SkillFile {
  rel_path: string;
  body: string;
  frontmatter: Record<string, unknown>;
  updated_at: string;
}

export interface SkillFilesResponse {
  content_hash: string;
  version_n: number;
  files: SkillFile[];
}

export interface MutationResponse {
  content_hash: string;
  version_n: number;
}

export interface RenameVariableResponse extends MutationResponse {
  affected_files: string[];
  summary: string;
}

export interface BrokerCallRequest {
  prompt: string;
  role: 'create-skill' | 'description-critic' | 'body-critic' | 'inline-rewrite';
  skillId?: string;
  context?: Record<string, unknown>;
}

export interface BrokerCallResponse {
  text: string;
  tokens: { input: number; output: number };
}

interface RawBrokerCall {
  skill_id: string;
  target: 'anthropic' | 'openai';
  model: string;
  rendered_prompt: string;
  variable_fingerprint: string;
  variables_used: never[];
  max_tokens?: number;
}

interface RawBrokerResponse {
  output: string;
  tokens_in: number;
  tokens_out: number;
  duration_ms: number;
  call_id: string;
}

const ROLE_PROMPTS: Record<BrokerCallRequest['role'], string> = {
  'create-skill':
    'You are drafting a Claude-Code skill in Markdown. Given the user request, produce a complete skill body with a clear opening line that names what the skill does. Do not include YAML frontmatter; the editor wraps that.',
  'description-critic':
    'You are a strict critic of skill descriptions. Suggest a tighter one-line description for the skill, or return the original if it is already tight.',
  'body-critic':
    'Critique the following skill body for clarity, missing edge cases, and unclear instructions. Be terse. List concrete improvements.',
  'inline-rewrite':
    'Rewrite the following passage. Keep the meaning. Tighten the prose.',
};

const ZERO_FINGERPRINT = '0'.repeat(64);

export interface PublishResult {
  url: string;
  sha: string;
  publishedAt: string;
}

export interface ScrubFinding {
  category:
    | 'declared_secret'
    | 'openai_key'
    | 'anthropic_key'
    | 'github_pat'
    | 'aws_key'
    | 'generic_high_entropy'
    | 'user_info_url'
    | 'email';
  start: number;
  end: number;
  match: string;
  replacement: string | null;
  severity: 'block' | 'warn' | 'info';
  placeholderName?: string;
}

export interface ScrubPreview {
  scrubbed_body: string;
  findings: ScrubFinding[];
  block_reasons: string[];
  warnings: string[];
  placeholder_count: number;
}

export interface UserVariable {
  id: string;
  name: string;
  type: 'secret' | 'text';
  value: string; // already masked for secrets
  description: string | null;
  updatedAt: string;
}

export interface VariableVaultDiff {
  matched: { name: string; type: 'secret' | 'text' }[];
  missing: string[];
  total: number;
}

export interface CommunitySkillSummary {
  id: string;
  name: string;
  description: string | null;
  source_kind: 'github' | 'skillsou_listed';
  source_url: string;
  source_repo: string | null;
  stars: number;
  last_updated: string;
  tags: string[];
}

export interface ImportResult {
  id: string;
  importedFrom: string;
}

export interface OrgSkillSummary {
  id: string;
  skillPath: string;
  latestCommitSha: string;
  scope: string;
  description: string | null;
  lastModifiedAt: string;
}

export interface OrgSkillDetail {
  content: string;
  commitSha: string;
  skillPath: string;
}

export interface TeamDraft {
  id: string;
  orgId: string;
  skillId: string;
  contributorUserId: string;
  baseVersionHash: string;
  body: string;
  state: 'draft' | 'submitted' | 'approved' | 'sent_back' | 'stale';
  note: string | null;
  summary: string | null;
  createdAt: string;
  submittedAt: string | null;
  decidedAt: string | null;
  decidedByUserId: string | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearSession();
    throw new ApiError('Unauthorized', 401, 'unauthorized');
  }
  if (!res.ok) {
    let payload: {
      code?: string;
      message?: string;
      issues?: Array<{ path?: (string | number)[]; message?: string }>;
    } = {};
    try {
      payload = (await res.json()) as typeof payload;
    } catch {
      // body wasn't json
    }
    const humanised =
      payload.message ??
      (payload.code === 'invalid_request' && payload.issues?.[0]?.message
        ? `Invalid input: ${payload.issues[0].message}`
        : payload.code === 'invalid_redirect_uri'
          ? 'This redirect URI is not allowed. Add the SPA origin to CORS_ALLOWED_ORIGINS on the API.'
          : `HTTP ${res.status}`);
    throw new ApiError(humanised, res.status, payload.code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiClient = {
  health: () => request<{ ok: boolean }>('/health'),

  skills: {
    list: () => request<PersonalSkillSummary[]>('/me/skills'),
    get: (id: string) => request<PersonalSkillDetail>(`/me/skills/${encodeURIComponent(id)}`),
    create: (input: { name: string; description?: string; body: string }) =>
      request<PersonalSkillDetail>('/me/skills', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    update: (
      id: string,
      input: { name?: string; description?: string; body?: string },
      opts: { expectedContentHash?: string } = {},
    ) =>
      request<PersonalSkillDetail>(`/me/skills/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: opts.expectedContentHash ? { 'If-Match': opts.expectedContentHash } : {},
        body: JSON.stringify(input),
      }),
    delete: (id: string) =>
      request<void>(`/me/skills/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },

  files: {
    list: (skillId: string) =>
      request<SkillFilesResponse>(`/me/skills/${encodeURIComponent(skillId)}/files`),
    create: (
      skillId: string,
      input: { rel_path: string; body: string; frontmatter?: Record<string, unknown> },
      expectedContentHash: string,
    ) =>
      request<MutationResponse>(`/me/skills/${encodeURIComponent(skillId)}/files`, {
        method: 'POST',
        body: JSON.stringify({ ...input, expected_content_hash: expectedContentHash }),
      }),
    update: (
      skillId: string,
      relPath: string,
      input: { body?: string; frontmatter?: Record<string, unknown> },
      expectedContentHash: string,
    ) =>
      request<MutationResponse>(
        `/me/skills/${encodeURIComponent(skillId)}/files/${relPath
          .split('/')
          .map(encodeURIComponent)
          .join('/')}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ ...input, expected_content_hash: expectedContentHash }),
        },
      ),
    delete: (skillId: string, relPath: string, expectedContentHash: string) =>
      request<MutationResponse>(
        `/me/skills/${encodeURIComponent(skillId)}/files/${relPath
          .split('/')
          .map(encodeURIComponent)
          .join('/')}`,
        {
          method: 'DELETE',
          headers: { 'If-Match': expectedContentHash },
        },
      ),
    renameVariable: (
      skillId: string,
      input: { old_name: string; new_name: string },
      expectedContentHash: string,
    ) =>
      request<RenameVariableResponse>(
        `/me/skills/${encodeURIComponent(skillId)}/rename-variable`,
        {
          method: 'POST',
          body: JSON.stringify({ ...input, expected_content_hash: expectedContentHash }),
        },
      ),
  },

  broker: {
    call: async (input: BrokerCallRequest): Promise<BrokerCallResponse> => {
      const payload: RawBrokerCall = {
        skill_id: input.skillId ?? `editor-${input.role}`,
        target: (import.meta.env.VITE_BROKER_TARGET as 'anthropic' | 'openai' | undefined) ?? 'openai',
        model:
          (import.meta.env.VITE_BROKER_MODEL as string | undefined) ?? 'gpt-4o-mini',
        rendered_prompt: `${ROLE_PROMPTS[input.role]}\n\n${input.prompt}`,
        variable_fingerprint: ZERO_FINGERPRINT,
        variables_used: [],
      };
      const raw = await request<RawBrokerResponse>('/broker/call', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return {
        text: raw.output,
        tokens: { input: raw.tokens_in, output: raw.tokens_out },
      };
    },
  },

  publish: {
    toGithub: (
      id: string,
      input: {
        target?: 'gist' | 'repo';
        token: string;
        description?: string;
        isPublic?: boolean;
        confirmedScrub?: boolean;
      },
    ) =>
      request<PublishResult>(`/me/skills/${encodeURIComponent(id)}/publish`, {
        method: 'POST',
        body: JSON.stringify({ target: 'gist', isPublic: true, ...input }),
      }),
    scrubPreview: (id: string) =>
      request<ScrubPreview>(`/me/skills/${encodeURIComponent(id)}/scrub-preview`),
    fromGithub: (input: { url: string }) =>
      request<ImportResult>('/me/skills/import', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  },

  community: {
    browse: (params: { q?: string; sort?: 'stars' | 'updated'; limit?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.q) qs.set('q', params.q);
      if (params.sort) qs.set('sort', params.sort);
      if (params.limit) qs.set('limit', String(params.limit));
      const suffix = qs.toString();
      return request<{ total: number; results: CommunitySkillSummary[] }>(
        `/community/browse${suffix ? `?${suffix}` : ''}`,
      );
    },
  },

  orgs: {
    listSkills: (orgId: string) =>
      request<{ skills: OrgSkillSummary[] }>(`/orgs/${encodeURIComponent(orgId)}/skills`),
    getSkill: (orgId: string, skillPath: string) =>
      request<OrgSkillDetail>(
        `/orgs/${encodeURIComponent(orgId)}/skills/${skillPath
          .split('/')
          .map(encodeURIComponent)
          .join('/')}`,
      ),
    drafts: {
      myOpenForSkill: (orgId: string, skillId: string) =>
        request<TeamDraft | null>(
          `/orgs/${encodeURIComponent(orgId)}/drafts/mine?skill_id=${encodeURIComponent(skillId)}`,
        ),
      start: (orgId: string, input: { skill_id: string; body: string; base_version_hash: string }) =>
        request<TeamDraft>(`/orgs/${encodeURIComponent(orgId)}/drafts`, {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      patch: (orgId: string, draftId: string, input: { body?: string; note?: string }) =>
        request<TeamDraft>(
          `/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(draftId)}`,
          { method: 'PATCH', body: JSON.stringify(input) },
        ),
      submit: (orgId: string, draftId: string, input: { note?: string } = {}) =>
        request<TeamDraft>(
          `/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(draftId)}/submit`,
          { method: 'POST', body: JSON.stringify(input) },
        ),
      get: (orgId: string, draftId: string) =>
        request<TeamDraft>(
          `/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(draftId)}`,
        ),
      inbox: (orgId: string) =>
        request<TeamDraft[]>(`/orgs/${encodeURIComponent(orgId)}/drafts`),
      approve: (orgId: string, draftId: string) =>
        request<{ ok: true; version_hash: string }>(
          `/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(draftId)}/approve`,
          { method: 'POST' },
        ),
      sendBack: (orgId: string, draftId: string, input: { note: string }) =>
        request<{ ok: true }>(
          `/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(draftId)}/send-back`,
          { method: 'POST', body: JSON.stringify(input) },
        ),
    },
  },

  variables: {
    list: () => request<UserVariable[]>('/me/variables'),
    create: (input: { name: string; type: 'secret' | 'text'; value: string; description?: string }) =>
      request<{ id: string; name: string; type: 'secret' | 'text' }>('/me/variables', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    update: (
      id: string,
      input: Partial<{ name: string; type: 'secret' | 'text'; value: string; description: string | null }>,
    ) =>
      request<{ ok: true }>(`/me/variables/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    delete: (id: string) =>
      request<void>(`/me/variables/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    vaultForSkill: (skillId: string) =>
      request<VariableVaultDiff>(
        `/me/skills/${encodeURIComponent(skillId)}/variable-vault`,
      ),
  },
};

export type ApiClient = typeof apiClient;
